import { useEffect, useState } from "react"
import './markdown.css'

// Minimal, dependency-free markdown renderer. Handles the constructs the AI produces:
// headings, ordered/unordered lists, code fences, blockquotes, horizontal rules, paragraphs,
// and inline **bold**, *italic*, `code`. Builds React elements (no dangerouslySetInnerHTML).

function renderInline(text, keyPrefix) {
    const nodes = []
    let remaining = text
    let key = 0
    const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/
    while (remaining.length) {
        const m = remaining.match(regex)
        if (!m) { nodes.push(remaining); break }
        if (m.index > 0) nodes.push(remaining.slice(0, m.index))
        if (m[2] != null) nodes.push(<strong key={`${keyPrefix}-b${key++}`}>{m[2]}</strong>)
        else if (m[3] != null) nodes.push(<em key={`${keyPrefix}-i${key++}`}>{m[3]}</em>)
        else if (m[4] != null) nodes.push(<code key={`${keyPrefix}-c${key++}`} className="md-inline-code">{m[4]}</code>)
        remaining = remaining.slice(m.index + m[0].length)
    }
    return nodes
}

function renderMarkdown(md) {
    const lines = (md ?? "").replace(/\r\n/g, '\n').split('\n')
    const blocks = []
    let i = 0
    let key = 0
    while (i < lines.length) {
        const line = lines[i]

        if (line.trim().startsWith('```')) {
            const code = []
            i++
            while (i < lines.length && !lines[i].trim().startsWith('```')) { code.push(lines[i]); i++ }
            i++
            blocks.push(<pre key={key++} className="md-code-block"><code>{code.join('\n')}</code></pre>)
            continue
        }

        const h = line.match(/^(#{1,6})\s+(.*)$/)
        if (h) {
            const level = Math.min(h[1].length, 6)
            const Tag = `h${level}`
            blocks.push(<Tag key={key++} className={`md-h md-h${level}`}>{renderInline(h[2], `h${key}`)}</Tag>)
            i++
            continue
        }

        if (/^(\s*[-*_]){3,}\s*$/.test(line)) { blocks.push(<hr key={key++} className="md-hr" />); i++; continue }

        if (line.trim().startsWith('>')) {
            const quote = []
            while (i < lines.length && lines[i].trim().startsWith('>')) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i++ }
            blocks.push(<blockquote key={key++} className="md-quote">{renderInline(quote.join(' '), `q${key}`)}</blockquote>)
            continue
        }

        if (/^\s*[-*+]\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*+]\s+/, '')); i++ }
            blocks.push(<ul key={key++} className="md-ul">{items.map((it, idx) => <li key={idx}>{renderInline(it, `ul${key}-${idx}`)}</li>)}</ul>)
            continue
        }

        if (/^\s*\d+\.\s+/.test(line)) {
            const items = []
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++ }
            blocks.push(<ol key={key++} className="md-ol">{items.map((it, idx) => <li key={idx}>{renderInline(it, `ol${key}-${idx}`)}</li>)}</ol>)
            continue
        }

        if (line.trim() === '') { i++; continue }

        const para = []
        while (
            i < lines.length &&
            lines[i].trim() !== '' &&
            !/^(#{1,6}\s|\s*[-*+]\s|\s*\d+\.\s|>|```)/.test(lines[i]) &&
            !/^(\s*[-*_]){3,}\s*$/.test(lines[i])
        ) { para.push(lines[i]); i++ }
        blocks.push(<p key={key++} className="md-p">{renderInline(para.join(' '), `p${key}`)}</p>)
    }
    return blocks
}

export default function Markdown({ content, className }) {
    return <div className={`markdown-body ${className ?? ''}`}>{renderMarkdown(content)}</div>
}

// Fetches markdown from a URL (the resource content URL) and renders it.
export function MarkdownFromUrl({ url, className }) {
    const [text, setText] = useState(null)
    const [error, setError] = useState(false)

    useEffect(() => {
        let active = true
        setText(null); setError(false)
        fetch(url)
            .then(r => { if (!r.ok) throw new Error('failed'); return r.text() })
            .then(t => { if (active) setText(t) })
            .catch(() => { if (active) setError(true) })
        return () => { active = false }
    }, [url])

    if (error) return <div className="markdown-body md-state">Could not load this content.</div>
    if (text == null) return <div className="markdown-body md-state">Loading content...</div>
    return <Markdown content={text} className={className} />
}
